'use client';

import { useMemo, useState } from 'react';
import { useProductReviews, useUserReviews } from '@/hooks/useReviews';
import { useCurrentOrders } from '@/hooks/useOrders';
import { useAuth } from '@/providers/AuthProvider';
import { RatingDistribution } from './RatingDistribution';
import { ReviewsList } from './ReviewsList';
import { ReviewForm } from './ReviewForm';

interface ProductReviewsSectionProps {
  productId: string;
}

const COMPLETED_STATUSES = ['Delivered', 'Picked Up', 'Fulfilled'];
const getId = (obj: any): string => obj?.id || obj?._id || '';
const getVariantId = (item: any): string => item?.productVariantId || item?._id || item?.id || '';
const reviewKey = (orderId: string, variantId: string) => `${orderId}:${variantId}`;

export function ProductReviewsSection({ productId }: ProductReviewsSectionProps) {
  const { reviews, isLoading, error, refetch } = useProductReviews(productId);
  const { isAuthenticated } = useAuth();
  const { orders } = useCurrentOrders();
  const { reviews: userReviews } = useUserReviews();

  const [showForm, setShowForm] = useState(false);

  // Build the set of already-reviewed order+variant combos
  const reviewedSet = useMemo(() => {
    const set = new Set<string>();
    userReviews.forEach((r: any) => {
      if (r.orderId && r.productVariantId) {
        set.add(reviewKey(r.orderId, r.productVariantId));
      }
    });
    return set;
  }, [userReviews]);

  // Find the first unreviewed completed order item for this product
  const pendingOrderItem = useMemo(() => {
    if (!isAuthenticated) return null;
    for (const order of orders) {
      if (!order.orderStatus || !COMPLETED_STATUSES.includes(order.orderStatus)) continue;
      const orderId = getId(order);
      for (const item of (order.items ?? [])) {
        const variantData = (item as any).productVariantData || (item as any).variant;
        const itemProductId = (item as any).productId || variantData?.productId;
        if (itemProductId !== productId) continue;
        const variantId = getVariantId(item);
        if (!reviewedSet.has(reviewKey(orderId, variantId))) {
          return { orderId, variantId };
        }
      }
    }
    return null;
  }, [isAuthenticated, orders, productId, reviewedSet]);

  const canReview = !!pendingOrderItem;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-2">Failed to load reviews</p>
        <p className="text-sm text-gray-500">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <RatingDistribution
        reviews={reviews}
        onWriteReview={canReview ? () => setShowForm(true) : undefined}
      />

      {showForm && pendingOrderItem && (
        <div className="rounded-xl border border-gray-200 p-6 bg-white">
          <h3 className="text-lg font-semibold text-hsecondary mb-6">Share Your Experience</h3>
          <ReviewForm
            productId={productId}
            productVariantId={pendingOrderItem.variantId}
            orderId={pendingOrderItem.orderId}
            onSuccess={() => {
              setShowForm(false);
              refetch();
            }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {reviews.length > 0 && 
      <ReviewsList reviews={reviews} isLoading={isLoading} />
      }
    </div>
  );
}
